import { cloneElement, useEffect, useState } from "react";
import { Head, Link } from "@inertiajs/react";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import ProductEditDrawer from "@/pages/products/product-edit-drawer";
import StepCreateDrawer from "@/pages/steps/step-create-drawer";
import StepEditDrawer from "@/pages/steps/step-edit-drawer";
import ProductKnowledgeFilesIndex from "@/pages/products/knowledge-files/index";

function IntroStepCard({ step, href = null, onEdit = null, actionLabel, actionVariant = "edit", materialLabel }) {
  const metrics = [
    step.substeps_count > 0 ? `${step.substeps_count} undertrin` : null,
    step.participants_count > 0 ? `${step.participants_count} ${step.participants_count === 1 ? "involveret" : "involverede"}` : null,
    step.templates_count > 0 ? `${step.templates_count} ${materialLabel(step.templates_count)}` : null
  ].filter(Boolean);
  const actionClassName = `btn btn-primary${actionVariant === "open" ? " workspace-step-intro__open-button" : ""}`;
  const action = href ? (
    <Link href={href} className={actionClassName}>
      {actionLabel}
    </Link>
  ) : onEdit ? (
    <button type="button" className={actionClassName} onClick={onEdit}>
      {actionLabel}
    </button>
  ) : null;

  return (
    <article id={`step-${step.id}`} className="card workspace-step-grid-card workspace-step-grid-card--intro">
      <div className="card-body workspace-step-intro__body">
        <div className="workspace-step-intro__content">
          <div className="workspace-card-eyebrow">Start her</div>
          <h3 className="workspace-card-title">{step.name}</h3>
          <p className="workspace-card-description workspace-step-intro__description">
            {step.description || "Få overblik over forløbet, før arbejdet med de enkelte trin begynder."}
          </p>

          {metrics.length > 0 ? (
            <div className="workspace-step-intro__metrics" aria-label="Indhold i introduktionen">
              {metrics.map((metric) => (
                <span key={metric} className="workspace-chip">{metric}</span>
              ))}
            </div>
          ) : null}
        </div>

        {action ? <div className="workspace-step-intro__action">{action}</div> : null}
      </div>
    </article>
  );
}

export function ProductShowPage({ product, stepHrefBuilder, editStepHrefBuilder = null, onCreateStep = null, onEditStep = null, onEditProduct = null, knowledgeFilesManage = false, usesTemplateTerminology = false }) {
  const materialLabel = (count) => {
    if (usesTemplateTerminology) return count === 1 ? "skabelon" : "skabeloner";

    return count === 1 ? "dokument" : "dokumenter";
  };
  const progressLabel = usesTemplateTerminology ? "Skabelonfremdrift" : "Dokumentfremdrift";

  return (
    <>
      <Head title={product.name} />

      <section className="workspace-content">
        <div className="row g-4">
          <div className="col-12">
            <div className="card workspace-hero workspace-product-summary workspace-product-summary--hero">
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between gap-4">
                  <div>
                    <div className="workspace-hero__badge">Produkt</div>
                    <h2 className="workspace-product-summary__title">Om produktet</h2>
                  </div>

                  {onEditProduct ? (
                    <button type="button" className="btn btn-light" onClick={onEditProduct}>
                      Redigér
                    </button>
                  ) : null}
                </div>

                <p className="workspace-product-summary__description">{product.description}</p>

                {product.template_progress?.total > 0 ? (
                  <div className="workspace-product-progress" aria-label={progressLabel}>
                    <div className="workspace-product-progress__header">
                      <span>Dokumenter udfyldt</span>
                      <strong>
                        {product.template_progress.completed} af {product.template_progress.total}
                      </strong>
                    </div>
                    <div className="workspace-product-progress__track" aria-hidden="true">
                      <div
                        className="workspace-product-progress__bar"
                        style={{ width: `${product.template_progress.percentage}%` }}
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {product.steps.map((step) => {
            const stepHref = stepHrefBuilder(product, step);
            const editStepHref = editStepHrefBuilder ? editStepHrefBuilder(product, step) : null;
            const isIntroStep = Number(step.index ?? step.number) === 0;

            if (isIntroStep) {
              return (
                <div key={step.id} className="col-12">
                  <IntroStepCard
                    step={step}
                    href={stepHref || editStepHref}
                    onEdit={onEditStep ? () => onEditStep(step.id) : null}
                    actionLabel={stepHref ? "Åbn introduktion" : "Redigér"}
                    actionVariant={stepHref ? "open" : "edit"}
                    materialLabel={materialLabel}
                  />
                </div>
              );
            }

            const canEditStep = Boolean(onEditStep || editStepHref);
            const actionColumns = [stepHref, canEditStep].filter(Boolean).length;
            const templateProgress = step.template_progress || { completed: 0, total: 0, percentage: 100 };

            return (
              <div key={step.id} className="col-md-6 col-xl-4">
                <article id={`step-${step.id}`} className="card workspace-step-grid-card workspace-step-grid-card--overview h-100">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex align-items-start justify-content-between gap-4">
                      <div>
                        <div className="workspace-card-eyebrow">{step.tag}</div>
                        <h3 className="workspace-card-title" title={step.name}>{step.name}</h3>
                      </div>
                    </div>

                    <p className="workspace-card-description" title={step.description}>{step.description}</p>

                    <div className="workspace-step-grid-card__footer mt-auto">
                      <div className="d-flex flex-wrap gap-3">
                        <span className="workspace-chip">{step.substeps_count} undertrin</span>
                        <span className="workspace-chip">{step.participants_count} involverede</span>
                        <span className="workspace-chip">{step.templates_count} {materialLabel(step.templates_count)}</span>
                      </div>

                      <div className="row g-2 pt-2">
                        {stepHref ? (
                          <div className={actionColumns > 1 ? "col-sm-6" : "col-12"}>
                            <Link href={stepHref} className="btn btn-primary w-100">
                              Se trin
                            </Link>
                          </div>
                        ) : null}
                        {editStepHref ? (
                          <div className={actionColumns > 1 ? "col-sm-6" : "col-12"}>
                            <Link href={editStepHref} className="btn btn-primary w-100">
                              Redigér
                            </Link>
                          </div>
                        ) : onEditStep ? (
                          <div className={actionColumns > 1 ? "col-sm-6" : "col-12"}>
                            <button
                              type="button"
                              className="btn btn-primary w-100"
                              onClick={() => onEditStep(step.id)}
                            >
                              Redigér
                            </button>
                          </div>
                        ) : null}
                      </div>

                      <div className="workspace-step-progress" aria-label={progressLabel}>
                        <div className="workspace-step-progress__header">
                          <span>Dokumenter udfyldt</span>
                          <strong>
                            {templateProgress.completed} af {templateProgress.total}
                          </strong>
                        </div>
                        <div className="workspace-step-progress__track" aria-hidden="true">
                          <div
                            className="workspace-step-progress__bar"
                            style={{ width: `${templateProgress.total === 0 ? 100 : templateProgress.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            );
          })}

          {onCreateStep ? (
            <div className="col-md-6 col-xl-4">
              <button type="button" className="card workspace-step-grid-card workspace-add-card h-100 text-start" onClick={onCreateStep}>
                <div className="card-body d-flex flex-column">
                  <div className="workspace-card-eyebrow">Nyt trin</div>
                  <h3 className="workspace-card-title">Tilføj trin</h3>

                  <p className="workspace-card-description">
                    Opret næste trin i forløbet og byg produktets efterlevelsesrejse videre.
                  </p>

                  <div className="workspace-step-grid-card__footer mt-auto">
                    <div className="pt-2">
                      <span className="btn btn-primary w-100">Tilføj trin</span>
                    </div>
                  </div>
                </div>
              </button>
            </div>
          ) : null}

          <div className="col-12">
            <ProductKnowledgeFilesIndex
              product={product}
              knowledge_files={product.knowledge_files || []}
              manage={knowledgeFilesManage}
              embedded
            />
          </div>
        </div>
      </section>
    </>
  );
}

export function ProductShowLayout({
  page,
  breadcrumbs,
  actions,
  updatePath,
  createStepPath,
  editStepPath,
  StepCreateDrawerComponent = StepCreateDrawer,
  StepEditDrawerComponent = StepEditDrawer
}) {
  const {
    product,
    templates = [],
    errors = [],
    step = null,
    step_errors: stepErrors = [],
    editable_steps: editableSteps = [],
    edit_step: editStep = null,
    edit_step_errors: editStepErrors = [],
    can_edit_product: canEditProduct = false,
    layout_header: layoutHeader,
    show_edit_drawer: showEditDrawer = false,
    show_create_step_drawer: showCreateStepDrawer = false,
    show_edit_step_drawer: showEditStepDrawer = false
  } = page.props;
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(canEditProduct && showEditDrawer);
  const [isCreateStepDrawerOpen, setIsCreateStepDrawerOpen] = useState(showCreateStepDrawer);
  const [activeEditStepId, setActiveEditStepId] = useState(editStep?.id || null);
  const [isEditStepDrawerOpen, setIsEditStepDrawerOpen] = useState(Boolean(showEditStepDrawer && editStep?.id));
  const [isEditDrawerSubmitting, setIsEditDrawerSubmitting] = useState(false);
  const [isCreateStepDrawerSubmitting, setIsCreateStepDrawerSubmitting] = useState(false);
  const [isEditStepDrawerSubmitting, setIsEditStepDrawerSubmitting] = useState(false);
  const editableStepsById = Object.fromEntries(editableSteps.map((currentStep) => [currentStep.id, currentStep]));

  if (editStep?.id) {
    editableStepsById[editStep.id] = editStep;
  }

  const activeEditStep = activeEditStepId ? editableStepsById[activeEditStepId] || null : null;

  useEffect(() => {
    if (canEditProduct && showEditDrawer) {
      setIsEditDrawerOpen(true);
    }
  }, [canEditProduct, showEditDrawer]);

  useEffect(() => {
    if (showCreateStepDrawer) {
      setIsCreateStepDrawerOpen(true);
    }
  }, [showCreateStepDrawer]);

  useEffect(() => {
    if (showEditStepDrawer && editStep?.id) {
      setActiveEditStepId(editStep.id);
      setIsEditStepDrawerOpen(true);
    }
  }, [editStep?.id, showEditStepDrawer]);

  return (
    <AuthLayout
      header={{
        breadcrumbs,
        title: layoutHeader?.title || product.name,
        actions: actions({
          product,
          canEditProduct,
          openEdit: () => setIsEditDrawerOpen(true),
          openCreateStep: () => setIsCreateStepDrawerOpen(true)
        })
      }}
    >
      {cloneElement(page, {
        onEditProduct: canEditProduct ? () => setIsEditDrawerOpen(true) : null,
        onCreateStep: canEditProduct ? () => setIsCreateStepDrawerOpen(true) : null,
        onEditStep: canEditProduct ? (stepId) => {
          setActiveEditStepId(stepId);
          setIsEditStepDrawerOpen(true);
        } : null
      })}
      {canEditProduct ? (
        <Drawer
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          title={`Redigér ${product.name}`}
          size="md"
          closeDisabled={isEditDrawerSubmitting}
        >
          <ProductEditDrawer
            product={product}
            errors={errors}
            updatePath={updatePath(product)}
            onSuccess={() => setIsEditDrawerOpen(false)}
            onProcessingChange={setIsEditDrawerSubmitting}
          />
        </Drawer>
      ) : null}
      {canEditProduct && createStepPath && step ? (
        <Drawer
          isOpen={isCreateStepDrawerOpen}
          onClose={() => setIsCreateStepDrawerOpen(false)}
          title={`Tilføj trin til ${product.name}`}
          size="lg"
          closeDisabled={isCreateStepDrawerSubmitting}
        >
          <StepCreateDrawerComponent
            step={step}
            templates={templates}
            errors={stepErrors}
            createPath={createStepPath(product)}
            onSuccess={() => setIsCreateStepDrawerOpen(false)}
            onProcessingChange={setIsCreateStepDrawerSubmitting}
          />
        </Drawer>
      ) : null}
      {canEditProduct && editStepPath && activeEditStep ? (
        <Drawer
          isOpen={isEditStepDrawerOpen}
          onClose={() => setIsEditStepDrawerOpen(false)}
          title={`Redigér ${activeEditStep.name}`}
          size="lg"
          closeDisabled={isEditStepDrawerSubmitting}
        >
          <StepEditDrawerComponent
            product={product}
            step={activeEditStep}
            templates={templates}
            errors={activeEditStep.id === editStep?.id ? editStepErrors : []}
            updatePath={editStepPath(product, activeEditStep)}
            onSuccess={() => setIsEditStepDrawerOpen(false)}
            onProcessingChange={setIsEditStepDrawerSubmitting}
          />
        </Drawer>
      ) : null}
    </AuthLayout>
  );
}
